import React from 'react';
import { Cpu, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const HomeTechnologies = () => {
  return (
    <>
      {/* Technologies Section */}
      <section id="technologies" className="py-24 bg-black relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1767202113040-6f8aa25112fb?q=80&w=2000)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Enabling</span> <span className="text-red-500">Technologies</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              VR / MR, AI & ML, Gaming Industry Software
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <Card className="bg-gradient-to-br from-red-950/30 to-black/70 border-red-900/50 backdrop-blur-sm mb-12">
              <CardContent className="pt-8">
                <p className="text-lg text-gray-300 text-center leading-relaxed">
                  Virtual Red Air leverages advanced digital technologies as force multipliers within Synthetic Training ecosystems, enhancing realism, adaptability, and training effectiveness across individual, collective, and multinational levels.
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-black/60 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-4 bg-red-600/20 rounded-lg">
                      <Eye className="h-10 w-10 text-red-500" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-white">Virtual and Mixed Reality - Artificial Intelligence & Machine Learning</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 text-gray-300">
                    <p className="leading-relaxed">
                      VRA integrates Virtual Reality and Mixed Reality technologies to deliver immersive, high-fidelity training experiences that enhance situational awareness, decision-making, and human performance.
                    </p>
                    <p className="leading-relaxed">
                      Our solutions enable realistic representation of complex operational environments, allowing trainees to rehearse missions, procedures, and coordination tasks in safe, repeatable, and cost-effective settings.
                    </p>
                    <p className="leading-relaxed">
                      Artificial Intelligence and Machine Learning will be central to VRA's approach to next-generation Synthetic Training.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-black/60 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-4 bg-red-600/20 rounded-lg">
                      <Cpu className="h-10 w-10 text-red-500" />
                    </div>
                    <CardTitle className="text-2xl text-white">Live Virtual Constructive Ecosystem - Gaming Industry Software</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-300 leading-relaxed space-y-4">
                    <p>Virtual Red Air does not treat technology as a standalone solution.</p>
                    <p>VR/MR, AI, and ML capabilities are selected, integrated, and employed as part of a coherent LVC Synthetic Training ecosystem aligned with operational requirements, interoperability standards, and multinational training objectives.</p>
                    <p>Using proven gaming technologies - real-time physics, multiplayer networking, rapid content creation, and persistent virtual worlds - we deliver simulations that are faster to iterate, cheaper to run, and far more immersive than traditional defense simulators.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why VRA Section */}
      <section id="why-vra" className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Why Virtual</span> <span className="text-red-500">Red Air</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mt-6">
              By combining advanced dual-use technologies with operational experience and ecosystem-centric design, VRA ensures that innovation directly contributes to readiness, interoperability, and mission success.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Globe,
                title: 'Deep NATO and Multinational Experience',
                desc: "VRA's personnel bring extensive experience from within NATO structures and multinational programmes, offering unique insight into alliance processes, interoperability requirements, and training needs."
              },
              {
                icon: Network,
                title: 'Ecosystem-Centric Approach',
                desc: 'Unlike traditional solution providers, Virtual Red Air focuses on building ecosystems rather than isolated systems-connecting people, platforms, and partners into coherent, interoperable training environments.'
              },
              {
                icon: Target,
                title: 'Operational Credibility',
                desc: "Our team's background in military operations, training, and simulation ensures credibility with end users and decision-makers alike, enabling solutions that are both realistic and operationally meaningful."
              },
              {
                icon: Briefcase,
                title: 'Independent & Trusted Partner',
                desc: 'As an independent consulting and brokerage entity, VRA acts in the interest of mission success-providing objective advice, aligning stakeholders, and enabling effective cooperation across organizational boundaries.'
              },
              {
                icon: Eye,
                title: 'Future-Oriented Perspective',
                desc: 'By combining experience, innovation, and collaboration, Virtual Red Air helps organizations anticipate future challenges and develop training capabilities that remain relevant in an evolving security environment.'
              },
            ].map((item, index) => (
              <Card key={index} className="bg-black/50 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all hover:transform hover:scale-105 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]">
                <CardHeader>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-4 bg-red-600/20 rounded-full">
                      <item.icon className="h-8 w-8 text-red-500" />
                    </div>
                    <CardTitle className="text-xl text-white">{item.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-400 text-center leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const Target = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const Globe = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const Network = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="16" y="16" width="6" height="6" rx="1"></rect>
    <rect x="2" y="16" width="6" height="6" rx="1"></rect>
    <rect x="9" y="2" width="6" height="6" rx="1"></rect>
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3"></path>
    <path d="M12 12V8"></path>
  </svg>
);

const Briefcase = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

export default HomeTechnologies;